// SPDX-License-Identifier: Apache-2.0
import GirafeHTMLElement from '../../base/GirafeHTMLElement';
import { UsedInTemplateOnly } from '../../decorators';
import { SearchCategories } from '../../tools/search/searchmanager';

type SearchFilterState = 'Idle' | 'CategoriesOpen' | 'ShortcutsOpen';

interface ShortcutEntry {
  shortcut: string;
  categoriesList: string;
}

interface CategoryEntry {
  key: string;
  provider: string;
  category: string;
  label: string;
  example?: string;
}

class SearchFilterComponent extends GirafeHTMLElement {
  templateUrl = './template.html';
  styleUrls = ['../../styles/common.css', './style.css'];

  public enabled = false;
  public currentPrefixSymbol: string | null = null;
  private prefixSymbols: string[] = [];
  private categories: SearchCategories = {};
  private availableShortcuts: { [shortcut: string]: string[] } = {};
  private selectedCategories: Map<string, CategoryEntry> = new Map();
  private filterExamples: Record<string, string> = {};
  private fsmState: SearchFilterState = 'Idle';
  private focusedShortcutIndex: number = -1;
  private readonly numberOfChipsToShow = 1;

  public constructor() {
    super('search-filter');
  }

  private registerEvents() {
    // Event listeners to close menu when clicking outside
    document.addEventListener('click', this.outsideClickHandler);
    this.addEventListener('click', this.insideClickHandler);
    // Event listeners for when shortcuts are entered directly in the search bar instead of selected in the menu
    this.subscribe('search.filterShortcutPrefix', (_old, prefix: string | null) => this.onPrefixUsed(prefix));
    this.subscribe('search.filterShortcut', (_old, shortcut: string | null) => this.applyShortcut(shortcut));
  }

  private async initSearchFilter() {
    const categories = await this.loadSearchCategories();
    if (!categories || !Object.values(categories).length) {
      this.enabled = false;
      this.refreshRender();
      return;
    }
    this.enabled = true;
    this.categories = categories;
    const searchConfig = this.context.configManager.Config.search;
    this.prefixSymbols = searchConfig?.filterShortcutPrefix ?? ['#'];
    this.currentPrefixSymbol = this.prefixSymbols[0];
    this.availableShortcuts = searchConfig?.filterShortcuts ?? {};
    this.filterExamples = searchConfig?.filterExamples ?? {};
    this.sanitizeShortcuts();
    this.refreshRender();
  }

  private async loadSearchCategories(): Promise<SearchCategories | null> {
    try {
      return (await this.context.searchManager.getSearchCategories()) ?? null;
    } catch (error) {
      console.error('Unable to load search categories:', error);
    }
    return null;
  }

  private sanitizeShortcuts() {
    // Remove categories that are not known to the search clients
    const availableCategories = this.getAllCategoriesFlattened();
    this.availableShortcuts = Object.fromEntries(
      Object.entries(this.availableShortcuts)
        .map(([shortcut, categories]) => [
          shortcut,
          categories.filter((category) => availableCategories.find((cat: CategoryEntry) => cat.category === category))
        ])
        .filter(([_shortcut, categories]) => categories.length > 0)
    );
  }

  @UsedInTemplateOnly()
  public getSelectedCategoryEntries(shortened: boolean = false): CategoryEntry[] {
    if (!this.isFilterActive()) {
      return [];
    }
    const selectedCategories = Array.from(this.selectedCategories.values());
    const hiddenLabelsCount = this.getNumberOfOverflowChips();
    if (!shortened || hiddenLabelsCount === 0) {
      return selectedCategories;
    }
    return selectedCategories.slice(0, this.numberOfChipsToShow);
  }

  public getNumberOfOverflowChips() {
    return Math.max(this.getSelectionCount() - this.numberOfChipsToShow, 0);
  }

  @UsedInTemplateOnly()
  public getNumberOfOverflowChipsAsLabel() {
    return this.context.i18nManager
      .getTranslation('+{number} more')
      .replace('{number}', String(this.getNumberOfOverflowChips()));
  }

  public isMenuOpen() {
    return this.fsmState !== 'Idle';
  }

  @UsedInTemplateOnly()
  public isCategoriesOpen() {
    return this.fsmState === 'CategoriesOpen';
  }

  public isShortcutsOpen() {
    return this.fsmState === 'ShortcutsOpen';
  }

  @UsedInTemplateOnly()
  public isCategorySelected(categoryEntry: CategoryEntry) {
    return this.selectedCategories.has(categoryEntry.key);
  }

  public hasShortcutsConfigured() {
    return Object.keys(this.availableShortcuts).length > 0;
  }

  public isFilterActive() {
    const totalCategories = this.getAllCategoriesFlattened().length;
    return totalCategories > 0 && this.selectedCategories.size > 0 && this.selectedCategories.size < totalCategories;
  }

  public getSelectionCount() {
    return this.isFilterActive() ? this.selectedCategories.size : 0;
  }

  public onFilterButtonClick(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.fsmState === 'CategoriesOpen') {
      this.closeMenu();
      return;
    }
    this.transitionTo('CategoriesOpen');
    this.refreshRender();
  }

  public onCategoryToggle(categoryEntry: CategoryEntry, e: Event) {
    const input = e.target as HTMLInputElement | null;
    if (!input) {
      return;
    }
    if (input.checked) {
      this.selectedCategories.set(categoryEntry.key, categoryEntry);
    } else {
      this.selectedCategories.delete(categoryEntry.key);
    }
    this.applyFiltersChange();
    this.refreshRender();
  }

  @UsedInTemplateOnly()
  public onSelectAll(event: Event) {
    event.preventDefault();
    this.selectedCategories = new Map(
      this.getAllCategoriesFlattened().map((categoryEntry) => [categoryEntry.key, categoryEntry])
    );
    this.applyFiltersChange();
    this.refreshRender();
  }

  @UsedInTemplateOnly()
  public onClearSelection(event: Event) {
    event.preventDefault();
    this.selectedCategories.clear();
    this.applyFiltersChange();
    this.refreshRender();
  }

  @UsedInTemplateOnly()
  public onChipClick(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.fsmState === 'CategoriesOpen') {
      this.closeMenu();
    } else {
      this.transitionTo('CategoriesOpen');
    }
    this.refreshRender();
  }

  @UsedInTemplateOnly()
  public onRemoveChip(categoryEntry: CategoryEntry, event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.selectedCategories.delete(categoryEntry.key);
    this.applyFiltersChange();
    this.refreshRender();
  }

  private closeMenu() {
    this.transitionTo('Idle');
    this.removeParentKeyDownHandler();
    this.focusedShortcutIndex = -1;
    this.refreshRender();
  }

  private transitionTo(state: SearchFilterState) {
    this.fsmState = state;
    // Update the state
    this.state.interface.searchFilterComponentVisible = this.fsmState !== 'Idle';
  }

  public getAllCategoriesFlattened(): CategoryEntry[] {
    return Object.entries(this.categories).flatMap(([provider, categoryList], _) =>
      categoryList.map((category: string) => {
        return this.createCategoryEntry(provider, category);
      })
    );
  }

  private createCategoryEntry(provider: string, category: string): CategoryEntry {
    return {
      key: `${provider}-${category}`,
      provider,
      category: category,
      label: this.context.searchManager.getI18nKeyForCategory(provider, category),
      example: this.filterExamples[category]
    };
  }

  private categoryEntriesToSearchCategories(): SearchCategories {
    return Array.from(this.selectedCategories.values()).reduce((categoriesPerProvider, entry) => {
      if (!categoriesPerProvider[entry.provider]) {
        categoriesPerProvider[entry.provider] = [];
      }
      categoriesPerProvider[entry.provider].push(entry.category);
      return categoriesPerProvider;
    }, {} as SearchCategories);
  }

  private applyFiltersChange() {
    this.state.search.filter = this.categoryEntriesToSearchCategories();
  }

  @UsedInTemplateOnly()
  public getShortcutEntries(): ShortcutEntry[] {
    return (
      Object.entries(this.availableShortcuts).map(([shortcut, categories]) => ({
        shortcut,
        categoriesList: categories.map((category) => this.translateCategory(category)).join(', ')
      })) ?? []
    );
  }

  private translateCategory(category: string): string {
    const provider = Object.keys(this.categories).find((provider) => this.categories[provider].includes(category));
    return this.context.i18nManager.getTranslation(
      provider ? this.context.searchManager.getI18nKeyForCategory(provider, category) : category
    );
  }

  @UsedInTemplateOnly()
  public getShortcutForCategory(categoryEntry: CategoryEntry) {
    const shortcut =
      Object.keys(this.availableShortcuts).find((shortcut) =>
        this.availableShortcuts[shortcut].includes(categoryEntry.category)
      ) ?? '';
    if (shortcut) {
      return `${this.currentPrefixSymbol}${shortcut}`;
    }
    return '';
  }

  private onPrefixUsed(prefix: string | null) {
    if (!this.hasShortcutsConfigured()) {
      return;
    }
    if (prefix === null) {
      // Prefix was removed from the search input
      this.closeMenu();
    } else {
      this.currentPrefixSymbol = prefix;
      this.transitionTo('ShortcutsOpen');
      this.addParentKeyDownEventListener();
    }
    this.refreshRender();
  }

  private readonly parentKeyDownHandler = (event: KeyboardEvent) => this.onParentKeyDown(event);
  /**
   * Triggered on a keydown event from the parent search input field.
   */
  public onParentKeyDown(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      this.closeMenu();
      return;
    }
    if (!this.isShortcutsOpen()) {
      return;
    }
    if (e.key === 'ArrowDown' && this.focusedShortcutIndex === -1) {
      // Focus the first shortcut
      this.focusedShortcutIndex = 0;
      this.focusResultFromIndex();
      this.removeParentKeyDownHandler();
    }
  }

  public onShortcutListDown(e: KeyboardEvent) {
    this.navigateToResult(e);
  }

  private navigateToResult(e: KeyboardEvent) {
    const elem = e.target as HTMLButtonElement;
    if (!elem) {
      return;
    }
    switch (e.key) {
      case 'Escape':
        this.closeMenu();
        break;

      case 'ArrowDown':
        if (this.focusedShortcutIndex < Object.keys(this.availableShortcuts).length - 1) {
          this.focusedShortcutIndex += 1;
          this.focusResultFromIndex();
        }
        break;

      case 'ArrowUp':
        if (this.focusedShortcutIndex > 0) {
          this.focusedShortcutIndex -= 1;
          this.focusResultFromIndex();
        }
        break;

      case 'Enter':
        this.applyShortcut(elem.dataset.shortcut ?? null);
        break;
    }
  }

  private focusResultFromIndex() {
    const shortcutList = this.getById('shortcut-list') as HTMLDivElement;
    if (!shortcutList) {
      return;
    }
    const shortcutBtn = [...shortcutList.children][this.focusedShortcutIndex] as HTMLButtonElement;
    if (shortcutBtn) {
      shortcutBtn.focus();
    }
  }

  private applyShortcut(shortcut: string | null) {
    if (!shortcut) {
      return;
    }
    const categoriesToActivate = this.availableShortcuts[shortcut] ?? [];
    categoriesToActivate.forEach((category) => {
      const entries = this.getAllCategoriesFlattened().filter((entry) => entry.category.includes(category));
      entries.forEach((entry) => this.selectedCategories.set(entry.key, entry));
    });

    if (categoriesToActivate) {
      this.closeMenu();
      this.applyFiltersChange();
    }
    this.refreshRender();
  }

  public onShortcutClick(shortcut: string) {
    this.applyShortcut(shortcut);
  }

  private readonly outsideClickHandler = (event: Event) => this.onOutsideClick(event);
  private onOutsideClick(_event: Event) {
    if (this.isMenuOpen()) {
      this.closeMenu();
    }
  }

  private readonly insideClickHandler = (event: Event) => this.onInsideClick(event);
  private onInsideClick(event: Event) {
    // Stop propagation to prevent closing menu when clicking inside the menu
    event.stopPropagation();
  }

  private removeClickHandlers() {
    document.removeEventListener('click', this.outsideClickHandler);
    this.removeEventListener('click', this.insideClickHandler);
  }

  private addParentKeyDownEventListener() {
    document.addEventListener('keydown', this.parentKeyDownHandler);
  }

  private removeParentKeyDownHandler() {
    document.removeEventListener('keydown', this.parentKeyDownHandler);
  }

  protected override connectedCallback() {
    super.connectedCallback();
    this.render();
    this.girafeTranslate();
    if (this.context.configManager.Config.search?.filterEnabled) {
      void this.initSearchFilter();
      this.registerEvents();
    } else {
      this.hide();
    }
  }

  protected override disconnectedCallback() {
    this.removeClickHandlers();
    this.removeParentKeyDownHandler();
    this.enabled = false;
    super.disconnectedCallback();
  }
}

export default SearchFilterComponent;
